// Composed sentiment-map view: the heat map renderer + the floating glass HUD
// (global summary, inverse callout, legend, breadcrumb) + loading/empty states.
// Phase 1 = national view; zoom + region detail land in Phase 2.
import './sentiment-map.scss';

import { useMemo, useState } from 'react';

import {
  CENSUS_REGION_BY_FIPS,
  CENSUS_REGIONS,
  type CensusRegion,
  STATE_NAME_BY_FIPS,
  stateFipsFromPath,
} from './geography';
import { rampColor } from './heatField';
import { SentimentHeatMap } from './SentimentHeatMap';
import type { SentimentMap as SentimentMapData } from './types';

// Seven sentiment bands for the legend/filter. `center` picks the swatch color
// off the same ramp the map uses, so the legend is literally the map's scale.
type BandKey = 'sNeg' | 'neg' | 'mNeg' | 'neu' | 'mPos' | 'pos' | 'sPos';
const BANDS: { key: BandKey; label: string; center: number }[] = [
  { key: 'sNeg', label: 'Strongly negative', center: -0.78 },
  { key: 'neg', label: 'Negative', center: -0.38 },
  { key: 'mNeg', label: 'Mildly negative', center: -0.18 },
  { key: 'neu', label: 'Neutral', center: 0 },
  { key: 'mPos', label: 'Mildly positive', center: 0.18 },
  { key: 'pos', label: 'Positive', center: 0.38 },
  { key: 'sPos', label: 'Strongly positive', center: 0.78 },
];
function bandOf(v: number): BandKey {
  if (v <= -0.5) return 'sNeg';
  if (v <= -0.25) return 'neg';
  if (v <= -0.12) return 'mNeg';
  if (v < 0.12) return 'neu';
  if (v < 0.25) return 'mPos';
  if (v < 0.5) return 'pos';
  return 'sPos';
}

interface Props {
  data: SentimentMapData | null;
  loading?: boolean;
  dark: boolean;
}

export function SentimentMap({ data, loading, dark }: Props) {
  const cls = `smap ${dark ? 'smap--dark' : 'smap--light'}`;

  if (loading) {
    return (
      <div className={cls}>
        <div className="smap__empty">Loading sentiment map…</div>
      </div>
    );
  }

  // "Substantial data" = at least one state-level region we can actually paint.
  // Without it there's nothing to render, so we show an inline message rather
  // than an empty map. The interaction (zoom/pan) only has meaning with data.
  const states = data?.regions.filter((r) => r.level === 1) ?? [];
  const mappable = states.filter((r) => r.sentiment != null);

  if (!data || mappable.length === 0) {
    return (
      <div className={cls}>
        <div className="smap__empty">
          <p className="smap__empty-title">
            Not enough data to map this story yet
          </p>
          <p className="smap__empty-sub">
            The sentiment map appears once enough readers in enough places have
            reacted. Check back soon.
          </p>
        </div>
      </div>
    );
  }

  const inverse = states.filter((r) => r.isOpposite);
  const fmt = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}`;

  return (
    <SentimentMapView
      data={data}
      dark={dark}
      inverse={inverse}
      fmt={fmt}
      cls={cls}
    />
  );
}

interface ViewProps {
  data: SentimentMapData;
  dark: boolean;
  inverse: SentimentMapData['regions'];
  fmt: (n: number) => string;
  cls: string;
}

// Plain-language reading of a mean valence (−1 hostile … +1 warm), so the box
// isn't a bare decimal. The pos/neutral/neg COUNT breakdown is TRIL-329 (backend).
function sentimentLabel(v: number): string {
  if (v >= 0.4) return 'strongly positive';
  if (v >= 0.12) return 'leans positive';
  if (v > -0.12) return 'balanced';
  if (v > -0.4) return 'leans negative';
  return 'strongly negative';
}

// Reaction split: a proportional negative/neutral/positive bar + counts (TRIL-329).
// Null until the ≥5 cohort, so it never appears for a sub-cohort region.
function Breakdown({
  neg,
  neu,
  pos,
}: {
  neg: number | null;
  neu: number | null;
  pos: number | null;
}) {
  if (neg == null || neu == null || pos == null) return null;
  const total = neg + neu + pos || 1;
  const parts = [
    neg ? `${neg} negative` : null,
    neu ? `${neu} neutral` : null,
    pos ? `${pos} positive` : null,
  ].filter(Boolean);
  return (
    <>
      <span className="smap__split" aria-hidden>
        <span
          className="smap__split-neg"
          style={{ width: `${(neg / total) * 100}%` }}
        />
        <span
          className="smap__split-neu"
          style={{ width: `${(neu / total) * 100}%` }}
        />
        <span
          className="smap__split-pos"
          style={{ width: `${(pos / total) * 100}%` }}
        />
      </span>
      <span className="smap__muted">{parts.join(' · ')}</span>
    </>
  );
}

function SentimentMapView({ data, dark, inverse, fmt, cls }: ViewProps) {
  const [selectedFips, setSelectedFips] = useState<string | null>(null);
  const [hover, setHover] = useState<{
    name: string;
    x: number;
    y: number;
    flip: boolean;
  } | null>(null);
  const [bands, setBands] = useState<BandKey[]>([]); // empty = all bands shown

  const selectedName = selectedFips
    ? (STATE_NAME_BY_FIPS[selectedFips] ?? 'Selected state')
    : null;
  const selectedRegion = selectedFips
    ? (data.regions.find(
        (r) => r.level === 1 && stateFipsFromPath(r.path) === selectedFips,
      ) ?? null)
    : null;

  // Additive region filter: empty = the whole map; pick regions to narrow the
  // headline + map to just those (recombined client-side from the enabled
  // regions' sufficient statistics — Σ meanᵣ·nᵣ / Σ nᵣ, no refetch). Selecting
  // all four folds back to empty (all = none = everything).
  const [enabled, setEnabled] = useState<CensusRegion[]>([]);
  const noRegionFilter = enabled.length === 0;

  let headline: number | null;
  let headlineCount: number;
  let hNeg: number | null;
  let hNeu: number | null;
  let hPos: number | null;
  if (noRegionFilter) {
    headline = data.sentimentG;
    headlineCount = data.nReplies;
    hNeg = data.nNegative;
    hNeu = data.nNeutral;
    hPos = data.nPositive;
  } else {
    let sum = 0;
    let n = 0;
    let neg = 0;
    let neu = 0;
    let pos = 0;
    for (const r of data.regions) {
      if (r.level !== 1 || r.sentiment == null) continue;
      const fips = stateFipsFromPath(r.path);
      const region = fips ? CENSUS_REGION_BY_FIPS[fips] : null;
      if (region && enabled.includes(region)) {
        sum += r.sentiment * r.nReplies;
        n += r.nReplies;
        neg += r.nNegative ?? 0;
        neu += r.nNeutral ?? 0;
        pos += r.nPositive ?? 0;
      }
    }
    headline = n > 0 ? sum / n : null;
    headlineCount = n;
    hNeg = neg;
    hNeu = neu;
    hPos = pos;
  }
  const scopeName = noRegionFilter
    ? 'United States'
    : CENSUS_REGIONS.filter((r) => enabled.includes(r)).join(' · ');

  // FIPS the filters allow on the map (null = no filter). Intersection of the
  // census-region filter (also drives the headline) and the sentiment-band
  // filter (map-only: isolate e.g. just the strongly-negative states).
  const allowedFips = useMemo(() => {
    const censusFilter = enabled.length > 0;
    const bandFilter = bands.length > 0;
    if (!censusFilter && !bandFilter) return null;
    const allowed = new Set<string>();
    for (const r of data.regions) {
      if (r.level !== 1 || r.sentiment == null) continue;
      const fips = stateFipsFromPath(r.path);
      if (!fips) continue;
      if (censusFilter) {
        const cr = CENSUS_REGION_BY_FIPS[fips];
        if (!cr || !enabled.includes(cr)) continue;
      }
      if (bandFilter && !bands.includes(bandOf(r.sentiment))) continue;
      allowed.add(fips);
    }
    return allowed;
  }, [enabled, bands, data.regions]);

  return (
    <div className={cls}>
      <div className="smap__stage">
        <SentimentHeatMap
          regions={data.regions}
          dark={dark}
          selectedFips={selectedFips}
          onSelectState={setSelectedFips}
          onHoverState={setHover}
          allowedFips={allowedFips}
        />

        {hover && (
          <div
            className={`smap__hover ${hover.flip ? 'smap__hover--left' : ''}`}
            style={{ left: hover.x, top: hover.y }}
          >
            {hover.name}
          </div>
        )}

        {allowedFips && allowedFips.size === 0 && (
          <div className="smap__glass smap__nomatch">
            No states fall in{' '}
            {bands.length === 1
              ? `the “${BANDS.find((b) => b.key === bands[0])?.label}” band`
              : 'the current filter'}
            .
          </div>
        )}

        <div className="smap__stack">
          <div className="smap__glass smap__summary">
            <span className="smap__region-name">{scopeName}</span>
            <span className="smap__big">
              {headline == null ? '—' : fmt(headline)}
            </span>
            <span className="smap__muted">
              {headline == null
                ? 'Not enough reactions yet'
                : `${sentimentLabel(headline)} · ${headlineCount.toLocaleString()} reactions`}
            </span>
            {headline != null && <Breakdown neg={hNeg} neu={hNeu} pos={hPos} />}
          </div>

          {selectedName && (
            <div className="smap__glass smap__region">
              <span className="smap__region-name">{selectedName}</span>
              {selectedRegion && selectedRegion.sentiment != null ? (
                <>
                  <span className="smap__big">
                    {fmt(selectedRegion.sentiment)}
                  </span>
                  <span className="smap__muted">
                    {sentimentLabel(selectedRegion.sentiment)} ·{' '}
                    {selectedRegion.nReplies.toLocaleString()} reactions
                  </span>
                  <Breakdown
                    neg={selectedRegion.nNegative}
                    neu={selectedRegion.nNeutral}
                    pos={selectedRegion.nPositive}
                  />
                  {selectedRegion.nVotes > 0 &&
                    selectedRegion.agreement != null && (
                      <span className="smap__muted">
                        {Math.round(selectedRegion.agreement * 100)}% agree ·{' '}
                        {selectedRegion.nVotes.toLocaleString()} votes
                      </span>
                    )}
                  {selectedRegion.isOpposite && (
                    <span className="smap__tag">
                      ⤬ runs against the national trend
                    </span>
                  )}
                  {selectedRegion.isLocalConsensus && (
                    <span className="smap__tag">◆ strong local consensus</span>
                  )}
                </>
              ) : (
                <span className="smap__muted">No data for this state yet</span>
              )}
            </div>
          )}
        </div>

        {inverse.length > 0 && (
          <div className="smap__glass smap__inverse">
            <span>
              ⤬ <b>{inverse.length} inverse</b> region
              {inverse.length > 1 ? 's' : ''}
            </span>
            <span className="smap__muted">
              {inverse.map((r) => r.path.split('/')[1]).join(', ')} runs against
              the trend
            </span>
          </div>
        )}

        <div className="smap__glass smap__filter">
          <span className="smap__muted">filter sentiment by region</span>
          <div className="smap__filter-chips">
            {CENSUS_REGIONS.map((region) => {
              const on = enabled.includes(region);
              return (
                <button
                  key={region}
                  type="button"
                  className={`smap__chip ${on ? 'smap__chip--on' : ''}`}
                  aria-pressed={on}
                  onClick={() =>
                    setEnabled((e) => {
                      const next = e.includes(region)
                        ? e.filter((x) => x !== region)
                        : [...e, region];
                      // all four selected = same as none = whole map → reset
                      return next.length === CENSUS_REGIONS.length ? [] : next;
                    })
                  }
                >
                  {region}
                </button>
              );
            })}
          </div>
        </div>

        <div className="smap__glass smap__legend">
          <div className="smap__band-chips">
            {BANDS.map((b) => {
              const [r, g, bl] = rampColor(b.center, dark);
              const dimmed = bands.length > 0 && !bands.includes(b.key);
              return (
                <button
                  key={b.key}
                  type="button"
                  className={`smap__band ${dimmed ? 'smap__band--off' : ''}`}
                  style={{ background: `rgb(${r}, ${g}, ${bl})` }}
                  title={b.label}
                  aria-label={b.label}
                  aria-pressed={bands.includes(b.key)}
                  onClick={() =>
                    setBands((p) =>
                      p.includes(b.key)
                        ? p.filter((x) => x !== b.key)
                        : [...p, b.key],
                    )
                  }
                />
              );
            })}
          </div>
          <span className="smap__muted">
            {bands.length === 0
              ? 'negative → positive · click to filter'
              : `${bands.length} band${bands.length > 1 ? 's' : ''} shown · click to clear`}
          </span>
        </div>

        <div className="smap__glass smap__crumb">
          <button
            type="button"
            className="smap__crumb-link"
            onClick={() => setSelectedFips(null)}
            disabled={!selectedFips}
          >
            United States
          </button>
          {selectedName ? (
            <span>&nbsp;▸ {selectedName}</span>
          ) : (
            <span className="smap__muted">&nbsp;·&nbsp; click a state</span>
          )}
        </div>
      </div>
    </div>
  );
}
